import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { hourWithMsRegex } from '@app/shared/custom-validators';
import { distinctUntilChanged } from 'rxjs/operators';

const formatNumber = (number: number, milli: boolean): string => {
  if (milli) {
    if (number.toString().length === 1) {
      return `00${number}`;
    }
    if (number.toString().length === 2) {
      return `0${number}`;
    }

    return `${number}`;
  }
  if (number.toString().length === 2) {
    return `${number}`;
  }

  return `0${number}`;
};

export const msToStringValue = (ms: number): string => {
  const t = ms / 1000;
  const h = Math.floor(t / 3600);
  const mns = Math.floor(t / 60) - h * 60;
  const secs = Math.floor(t) - mns * 60 - h * 3600;
  const mls = Math.round((t % 1) * 1000);

  return (
    [h, mns, secs].map(v => formatNumber(v, false)).join(':') +
    '.' +
    formatNumber(mls, true)
  );
};
@Component({
  selector: 'rk-cue-input',
  templateUrl: './cue-input.component.html',
  styleUrls: ['./cue-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CueInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CueInputComponent implements OnInit, ControlValueAccessor {
  cueForm: UntypedFormGroup;
  @Input()
  colorClass = 'inout';

  @Input()
  label: string;

  @Output()
  readonly setValueAtCursor = new EventEmitter();

  @Output()
  readonly resetValue = new EventEmitter();

  @Input()
  minValue: string;

  @Input()
  maxValue: string;

  @Input()
  value: string;

  timeSplit = ['00', '00', '00', '000'];

  numbersInRow = 0;
  currentSegment = 0;
  keepSegment = false;
  authorizedKey = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'ArrowRight',
    'ArrowLeft',
    'ArrowDown',
    'ArrowUp',
  ];
  @ViewChild('inputTime', { static: true }) input: ElementRef;

  constructor(private readonly fb: UntypedFormBuilder) {}

  ngOnInit() {
    this.cueForm = this.fb.group(
      {
        value: ['', Validators.pattern(hourWithMsRegex)],
      },
      { updateOn: 'blur' },
    );
  }

  getClass() {
    return {
      [this.colorClass]: true,
    };
  }

  clickOnTrash(e: Event) {
    this.resetValue.emit();
    e.stopPropagation();
  }

  valueToMilliseconds(formData: any): number | void {
    if (this.cueForm.valid) {
      const [h, m, s, ms] = formData.value.split(/[.:]/);

      return (
        parseInt(ms || 0, 10) +
        parseInt(s || 0, 10) * 1000 +
        parseInt(m || 0, 10) * 60 * 1000 +
        parseInt(h || 0, 10) * 60 * 60 * 1000
      );
    }
  }

  registerOnChange(fn: any): void {
    this.cueForm.valueChanges.pipe(distinctUntilChanged()).subscribe(val => {
      if (this.cueForm.valid) {
        return fn(this.valueToMilliseconds(val));
      }
    });
  }

  registerOnTouched(_: any): void {}

  setDisabledState(_: boolean): void {}

  writeValue(value: any): void {
    if (value !== undefined && this.numbersInRow === 0) {
      this.cueForm.get('value').setValue(msToStringValue(value));
    }
    this.timeSplit = this.cueForm.get('value').value.split(/[.:]/);
  }

  onBlur() {
    this.numbersInRow = 0;
    this.applyValue();
  }

  //  Make input component way smarter to handle time input
  onKeydown(e: KeyboardEvent) {
    e.preventDefault();
    if (this.authorizedKey.filter(key => e.key === key).length === 0) {
      return;
    }

    this.keepSegment = false;
    const selectedIndex = this.input.nativeElement.selectionStart;

    switch (e.key) {
      case 'ArrowLeft':
        this.numbersInRow = 0;
        if (selectedIndex < 3) {
          this.input.nativeElement.selectionStart = 9;
          this.input.nativeElement.selectionEnd = 12;
        } else if (selectedIndex < 6) {
          this.input.nativeElement.selectionStart = 0;
          this.input.nativeElement.selectionEnd = 2;
        } else if (selectedIndex < 9) {
          this.input.nativeElement.selectionStart = 3;
          this.input.nativeElement.selectionEnd = 5;
        } else {
          this.input.nativeElement.selectionStart = 6;
          this.input.nativeElement.selectionEnd = 8;
        }
        this.currentSegment = this.getSegmentForIndex(
          this.input.nativeElement.selectionStart,
        );
        break;
      case 'ArrowRight':
        this.numbersInRow = 0;
        if (selectedIndex < 3) {
          this.input.nativeElement.selectionStart = 3;
          this.input.nativeElement.selectionEnd = 5;
        } else if (selectedIndex < 6) {
          this.input.nativeElement.selectionStart = 6;
          this.input.nativeElement.selectionEnd = 8;
        } else if (selectedIndex < 9) {
          this.input.nativeElement.selectionStart = 9;
          this.input.nativeElement.selectionEnd = 12;
        } else {
          this.input.nativeElement.selectionStart = 0;
          this.input.nativeElement.selectionEnd = 2;
        }
        this.currentSegment = this.getSegmentForIndex(
          this.input.nativeElement.selectionStart,
        );
        break;
      case 'ArrowUp':
        this.keepSegment = true;
        this.updateWithArrow(+window.getSelection().toString() + 1);
        break;
      case 'ArrowDown':
        this.keepSegment = true;
        this.updateWithArrow(+window.getSelection().toString() - 1);
        break;
      default:
        this.updateWithNumber(+e.key);
    }

    return;
  }

  updateWithNumber(val: number) {
    if (this.currentSegment === 0) {
      const segmentValue = this.updateTmpSegmentValue(this.timeSplit[0], val);
      this.updateHour(segmentValue, true);
    } else if (this.currentSegment === 1) {
      const segmentValue = this.updateTmpSegmentValue(this.timeSplit[1], val);
      this.updateMinute(segmentValue, true);
    } else if (this.currentSegment === 2) {
      const segmentValue = this.updateTmpSegmentValue(this.timeSplit[2], val);
      this.updateSecond(segmentValue, true);
    } else {
      let segmentValue = this.timeSplit[3];
      if (this.numbersInRow === 0) {
        segmentValue = segmentValue.slice(0, 2) + val;
        this.numbersInRow += 1;
      } else if (this.numbersInRow === 1) {
        segmentValue = segmentValue.slice(0, 1) + segmentValue.slice(2, 3) + val;
        this.numbersInRow += 1;
      } else {
        segmentValue = segmentValue.slice(1, 3) + val;
        this.numbersInRow = 0;
      }
      this.updateMs(+segmentValue);
    }
  }

  updateTmpSegmentValue(seg: string, val: number): number {
    let segmentValue = seg;
    if (this.numbersInRow === 0) {
      segmentValue = segmentValue.slice(0, 1) + val;
      this.numbersInRow += 1;
    } else {
      segmentValue = segmentValue.slice(1, 2) + val;
      this.numbersInRow = 0;
    }

    return +segmentValue;
  }

  updateWithArrow(val: number) {
    if (this.currentSegment === 0) {
      this.updateHour(val, true);
    } else if (this.currentSegment === 1) {
      this.updateMinute(val, true);
    } else if (this.currentSegment === 2) {
      this.updateSecond(val, true);
    } else {
      this.updateMs(val);
    }
    this.numbersInRow = 0;
  }

  updateHour(value: number, apply = false) {
    if (value < 0) {
      this.replaceValue(0, 0, 2, false, apply);
    } else {
      this.replaceValue(value, 0, 2, false, apply);
    }
  }

  updateMinute(value: number, apply = false) {
    if (value > 59) {
      this.updateHour(+this.timeSplit[0] + 1);
      this.replaceValue(value % 60, 3, 5, false, apply);
    } else if (value < 0) {
      if (+this.timeSplit[0] === 0) {
        this.replaceValue(0, 3, 5, false, apply);
      } else {
        this.updateHour(+this.timeSplit[0] - 1);
        this.replaceValue(59, 3, 5, false, apply);
      }
    } else {
      this.replaceValue(value, 3, 5, false, apply);
    }
  }

  updateSecond(value: number, apply = false) {
    if (value > 59) {
      this.updateMinute(+this.timeSplit[1] + 1);
      this.replaceValue(value % 60, 6, 8, false, apply);
    } else if (value < 0) {
      if (+this.timeSplit[0] === 0 && +this.timeSplit[1] === 0) {
        this.replaceValue(0, 6, 8, false, apply);
      } else {
        this.updateMinute(+this.timeSplit[1] - 1);
        this.replaceValue(59, 6, 8, false, apply);
      }
    } else {
      this.replaceValue(value, 6, 8, false, apply);
    }
  }

  updateMs(value: number) {
    if (value > 999) {
      this.updateSecond(+this.timeSplit[2] + 1);
      this.replaceValue(0, 9, 12, true, true);
    } else if (value < 0) {
      if (
        +this.timeSplit[0] === 0 &&
        +this.timeSplit[1] === 0 &&
        +this.timeSplit[2] === 0
      ) {
        this.replaceValue(0, 9, 12, true, true);
      } else {
        this.updateSecond(+this.timeSplit[2] - 1);
        this.replaceValue(999, 9, 12, true, true);
      }
    } else {
      this.replaceValue(value, 9, 12, true, true);
    }
  }

  replaceValue(value: number, start: number, end: number, ms = false, apply = false) {
    let strVal = value.toString();
    if (ms) {
      if (value < 10) {
        strVal = '00' + strVal;
      } else if (value < 100) {
        strVal = '0' + strVal;
      }
    } else {
      if (value < 10) {
        strVal = '0' + strVal;
      }
    }
    this.timeSplit[this.getSegmentForIndex(start)] = strVal;
    if (apply) {
      this.applyValue();
    }
  }

  applyValue() {
    let finalVal =
      this.timeSplit[0] +
      ':' +
      this.timeSplit[1] +
      ':' +
      this.timeSplit[2] +
      '.' +
      this.timeSplit[3];
    if (this.numbersInRow === 0) {
      if (this.minValue && this.isGreater(this.minValue, finalVal)) {
        finalVal = this.minValue;
      } else if (this.maxValue && this.isGreater(finalVal, this.maxValue)) {
        finalVal = this.maxValue;
      }
      if (!this.keepSegment) {
        this.currentSegment += 1;
      }
    }
    this.input.nativeElement.value = finalVal;
    this.cueForm.get('value').setValue(this.input.nativeElement.value);
    this.onClick(this.firstIndexForSegment(), false);
  }

  isGreater(val1: string, val2: string): boolean {
    const val1Split = val1.split(/[.:]/);
    const val2Split = val2.split(/[.:]/);
    let g: boolean;
    val1Split.forEach((val: string, i: number) => {
      if (g !== undefined) {
        return;
      }
      if (val > val2Split[i]) {
        g = true;

        return;
      }
      if (val < val2Split[i]) {
        g = false;

        return;
      }
    });

    return !!g;
  }

  getSegmentForIndex(i: number): number {
    if (i < 3) {
      return 0;
    }
    if (i < 6) {
      return 1;
    }
    if (i < 9) {
      return 2;
    }

    return 3;
  }

  firstIndexForSegment(): number {
    if (this.currentSegment === 0) {
      return 0;
    }
    if (this.currentSegment === 1) {
      return 3;
    }
    if (this.currentSegment === 2) {
      return 6;
    }

    return 9;
  }

  onClick(forceIndex = -1, setSegment = true) {
    const selectedIndex =
      forceIndex > -1 ? forceIndex : this.input.nativeElement.selectionStart;
    if (selectedIndex < 3) {
      this.input.nativeElement.selectionStart = 0;
      this.input.nativeElement.selectionEnd = 2;
    } else if (selectedIndex < 6) {
      this.input.nativeElement.selectionStart = 3;
      this.input.nativeElement.selectionEnd = 5;
    } else if (selectedIndex < 9) {
      this.input.nativeElement.selectionStart = 6;
      this.input.nativeElement.selectionEnd = 8;
    } else {
      this.input.nativeElement.selectionStart = 9;
      this.input.nativeElement.selectionEnd = 12;
    }
    if (setSegment) {
      this.currentSegment = this.getSegmentForIndex(selectedIndex);
    }
  }
}
